import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, CheckCircle2, Database, FlaskConical, Link2, ListChecks, Plus, Rows3, Search, Settings2, ShieldCheck, Trash2 } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Input, NativeSelect, Switch, Textarea, cn, notifySaved } from "@ncp/ui";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { ConditionBuilder, describeCondition, type ConditionField } from "../features/generic/ConditionBuilder";
import { fieldKeyFromLabel, useDefinitionList, type DefinitionSummary } from "../features/generic/api";
import { RECORD_FIELD_TYPES, useRecordActions, useRecords, type DataRecord, type RecordCheck, type RecordField, type RecordFieldType, type RecordIssue, type RecordRelation, type RecordTypeBody } from "../features/records/api";
import { RecordForm, fieldLabel } from "../features/records/RecordForm";
import { useWorkspaces } from "../features/workspaces/api";

type T = (k: string, o?: Record<string, unknown>) => string;
type Ctx = EditorContext<RecordTypeBody>;
const KEY = /^[a-z][a-zA-Z0-9_]{0,63}$/;

function lint(b: RecordTypeBody, t: T): { errors: string[]; warnings: string[] } {
  const errors: string[] = []; const warnings: string[] = [];
  const seen = new Set<string>();
  for (const f of b.fields) {
    if (!KEY.test(f.key)) errors.push(t("admin:recordTypes.lint.fieldKey", { key: f.key || "?" }));
    else if (seen.has(f.key)) errors.push(t("admin:recordTypes.lint.fieldTwice", { key: f.key }));
    seen.add(f.key);
    if (!f.label.trim()) errors.push(t("admin:recordTypes.lint.fieldLabel", { key: f.key }));
    if (f.type === "choice" && !(f.options ?? []).some((o) => o.value.trim())) errors.push(t("admin:recordTypes.lint.options", { key: f.key }));
    if (f.type === "reference" && !f.target) errors.push(t("admin:recordTypes.lint.target", { key: f.key }));
    if (f.min != null && f.max != null && f.min > f.max) errors.push(t("admin:recordTypes.lint.range", { key: f.key }));
  }
  for (const r of b.relations) {
    if (!KEY.test(r.key)) errors.push(t("admin:recordTypes.lint.linkKey", { key: r.key || "?" }));
    else if (seen.has(r.key)) errors.push(t("admin:recordTypes.lint.fieldTwice", { key: r.key }));
    seen.add(r.key);
    if (!r.target) errors.push(t("admin:recordTypes.lint.linkTarget", { key: r.key }));
  }
  if (!/^[A-Z][A-Z0-9]{1,7}$/.test(b.numberPrefix)) errors.push(t("admin:recordTypes.lint.prefix"));
  if (b.titleField && !b.fields.some((f) => f.key === b.titleField)) errors.push(t("admin:recordTypes.lint.titleField"));
  for (const c of b.rules) if (!c.messageEn.trim()) warnings.push(t("admin:recordTypes.lint.checkMessage", { key: c.key }));
  if (b.fields.length === 0) warnings.push(t("admin:recordTypes.lint.noFields"));
  return { errors, warnings };
}

/** Record Type designer: fields, links, checks, settings, a "try a record" bench validated on the server, and the records kept in the type. */
export function RecordTypeDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<RecordTypeBody>
      resource="record-types"
      basePath="/data"
      navLabel={t("admin:nav.data")}
      icon={Database}
      writePermission="platform.recordtypes.write"
      docsAnchor="record-types"
      help={<><p>{t("admin:recordTypes.help.what")}</p><p>{t("admin:recordTypes.help.checks")}</p><p>{t("admin:recordTypes.help.extension")}</p></>}
      tabs={[
        { key: "fields", label: t("admin:recordTypes.tabs.fields"), icon: ListChecks },
        { key: "links", label: t("admin:recordTypes.tabs.links"), icon: Link2 },
        { key: "checks", label: t("admin:recordTypes.tabs.checks"), icon: ShieldCheck },
        { key: "settings", label: t("admin:recordTypes.tabs.settings"), icon: Settings2 },
        { key: "try", label: t("admin:recordTypes.tabs.try"), icon: FlaskConical },
        { key: "records", label: t("admin:recordTypes.tabs.records"), icon: Rows3 },
      ]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:recordTypes.summary", { fields: b.fields.length, links: b.relations.length, checks: b.rules.length, prefix: b.numberPrefix })}
      badges={(b) => b.extendsServiceKey ? <Badge variant="camel" size="sm">{t("admin:recordTypes.extends", { service: b.extendsServiceKey })}</Badge> : null}
      render={(tab, ctx) => (
        <>
          {tab === "fields" ? <FieldsTab ctx={ctx} /> : null}
          {tab === "links" ? <LinksTab ctx={ctx} /> : null}
          {tab === "checks" ? <ChecksTab ctx={ctx} /> : null}
          {tab === "settings" ? <SettingsTab ctx={ctx} /> : null}
          {tab === "try" ? <TryTab ctx={ctx} /> : null}
          {tab === "records" ? <RecordsTab ctx={ctx} /> : null}
        </>
      )}
    />
  );
}

// ---------------------------------------------------------------- fields

function FieldsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const [selected, setSelected] = useState(0);
  const [newType, setNewType] = useState<RecordFieldType>("text");
  const types = useDefinitionList("record-types");
  const field = body.fields[selected];

  const setField = (i: number, p: Partial<RecordField>) => patch((b) => ({ ...b, fields: b.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) }));
  const move = (i: number, d: -1 | 1) => patch((b) => {
    const next = [...b.fields]; const j = i + d;
    if (j < 0 || j >= next.length) return b;
    [next[i], next[j]] = [next[j]!, next[i]!];
    setSelected(j);
    return { ...b, fields: next };
  });
  const add = () => {
    const n = body.fields.length + 1;
    const f: RecordField = { key: `field${n}`, label: t("admin:recordTypes.fields.newLabel", { n }), labelAr: null, type: newType, required: false, searchable: true, listed: n <= 4, options: newType === "choice" ? [{ value: "a", label: "A" }] : null };
    patch((b) => ({ ...b, fields: [...b.fields, f] }));
    setSelected(body.fields.length);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      <Card>
        <CardHeader><CardTitle>{t("admin:recordTypes.tabs.fields")}</CardTitle><CardDescription>{t("admin:recordTypes.fields.intro")}</CardDescription></CardHeader>
        <CardContent className="space-y-1.5">
          {body.fields.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:recordTypes.fields.none")}</p> : null}
          {body.fields.map((f, i) => (
            <div key={i} data-testid={`field-row-${f.key}`} role="button" tabIndex={0} onClick={() => setSelected(i)} onKeyDown={(e) => { if (e.key === "Enter") setSelected(i); }}
              className={cn("flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors", i === selected ? "border-aegold-300 bg-aegold-50" : "border-sand-200 hover:bg-sand-50")}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{fieldLabel(f, locale)}{f.required ? <span className="text-danger-600"> *</span> : null}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground"><code dir="ltr">{f.key}</code> · {t(`admin:recordTypes.types.${f.type}`)}{body.titleField === f.key ? <Badge variant="gold" size="sm">{t("admin:recordTypes.fields.title")}</Badge> : null}</div>
              </div>
              {!readOnly ? (
                <div className="flex shrink-0">
                  <Button size="sm" variant="ghost" aria-label={t("admin:recordTypes.fields.up")} disabled={i === 0} onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUp /></Button>
                  <Button size="sm" variant="ghost" aria-label={t("admin:recordTypes.fields.down")} disabled={i === body.fields.length - 1} onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDown /></Button>
                </div>
              ) : null}
            </div>
          ))}
          {!readOnly ? (
            <div className="flex gap-2 pt-2">
              <NativeSelect aria-label={t("admin:recordTypes.fields.type")} value={newType} className="h-9 flex-1" onChange={(e) => setNewType(e.target.value as RecordFieldType)}>
                {RECORD_FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{t(`admin:recordTypes.types.${ty}`)}</option>)}
              </NativeSelect>
              <Button data-testid="add-field" variant="gold" size="sm" onClick={add}><Plus /> {t("admin:recordTypes.fields.add")}</Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {field ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div><CardTitle>{fieldLabel(field, locale)}</CardTitle><CardDescription>{t(`admin:recordTypes.typeHelp.${field.type}`)}</CardDescription></div>
            {!readOnly ? <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => { patch((b) => ({ ...b, fields: b.fields.filter((_, j) => j !== selected), titleField: b.titleField === field.key ? null : b.titleField })); setSelected(Math.max(0, selected - 1)); }}><Trash2 /> {t("admin:common.remove")}</Button> : null}
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:recordTypes.fields.label")} required><Input data-testid="field-label" value={field.label} disabled={readOnly} onChange={(e) => { const label = e.target.value; const auto = field.key === fieldKeyFromLabel(field.label) || /^field\d+$/.test(field.key); setField(selected, auto ? { label, key: fieldKeyFromLabel(label) || field.key } : { label }); }} /></Field>
            <Field label={t("admin:recordTypes.fields.labelAr")}><Input data-testid="field-label-ar" dir="rtl" value={field.labelAr ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { labelAr: e.target.value || null })} /></Field>
            <Field label={t("admin:common.key")} hint={t("admin:recordTypes.fields.keyHint")}><Input data-testid="field-key" dir="ltr" className="font-mono" value={field.key} disabled={readOnly} onChange={(e) => setField(selected, { key: e.target.value })} /></Field>
            <Field label={t("admin:recordTypes.fields.type")}>
              <NativeSelect data-testid="field-type" value={field.type} disabled={readOnly} onChange={(e) => { const type = e.target.value as RecordFieldType; setField(selected, { type, options: type === "choice" ? field.options ?? [{ value: "a", label: "A" }] : null }); }}>
                {RECORD_FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{t(`admin:recordTypes.types.${ty}`)}</option>)}
              </NativeSelect>
            </Field>
            <Field label={t("admin:recordTypes.fields.help")}><Input value={field.help ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { help: e.target.value || null })} /></Field>
            <Field label={t("admin:recordTypes.fields.helpAr")}><Input dir="rtl" value={field.helpAr ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { helpAr: e.target.value || null })} /></Field>

            {field.type === "number" || field.type === "money" ? (<>
              <Field label={t("admin:recordTypes.fields.min")}><Input data-testid="field-min" dir="ltr" inputMode="decimal" value={field.min ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { min: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
              <Field label={t("admin:recordTypes.fields.max")}><Input dir="ltr" inputMode="decimal" value={field.max ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { max: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
            </>) : null}
            {field.type === "text" || field.type === "longText" ? (<>
              <Field label={t("admin:recordTypes.fields.maxLength")}><Input dir="ltr" inputMode="numeric" value={field.maxLength ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { maxLength: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
              <Field label={t("admin:recordTypes.fields.pattern")} hint={t("admin:recordTypes.fields.patternHint")}><Input dir="ltr" className="font-mono" value={field.pattern ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { pattern: e.target.value || null })} /></Field>
            </>) : null}
            {field.type === "reference" ? (
              <Field label={t("admin:recordTypes.fields.target")}>
                <NativeSelect value={field.target ?? ""} disabled={readOnly} onChange={(e) => setField(selected, { target: e.target.value || null })}>
                  <option value="">—</option>
                  {(types.data ?? []).filter((x) => x.key !== ctx.definition.key).map((x) => <option key={x.key} value={x.key}>{locale === "ar" && x.nameAr ? x.nameAr : x.name}</option>)}
                </NativeSelect>
              </Field>
            ) : null}
            {field.type === "choice" ? <OptionsEditor field={field} readOnly={readOnly} onChange={(options) => setField(selected, { options })} /> : null}

            <div className="flex flex-wrap gap-4 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="field-required" checked={!!field.required} disabled={readOnly} onCheckedChange={(c) => setField(selected, { required: c })} /> {t("admin:recordTypes.fields.required")}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={field.searchable !== false} disabled={readOnly} onCheckedChange={(c) => setField(selected, { searchable: c })} /> {t("admin:recordTypes.fields.searchable")}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={field.listed !== false} disabled={readOnly} onCheckedChange={(c) => setField(selected, { listed: c })} /> {t("admin:recordTypes.fields.listed")}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={body.titleField === field.key} disabled={readOnly} onCheckedChange={(c) => patch({ titleField: c ? field.key : null })} /> {t("admin:recordTypes.fields.useAsTitle")}</label>
            </div>
          </CardContent>
        </Card>
      ) : <EmptyState icon={ListChecks} title={t("admin:recordTypes.fields.emptyTitle")} description={t("admin:recordTypes.fields.emptyBody")} />}
    </div>
  );
}

function OptionsEditor({ field, readOnly, onChange }: { field: RecordField; readOnly: boolean; onChange: (o: NonNullable<RecordField["options"]>) => void }) {
  const { t } = useTranslation();
  const options = field.options ?? [];
  const set = (i: number, p: Partial<(typeof options)[number]>) => onChange(options.map((o, j) => (j === i ? { ...o, ...p } : o)));
  return (
    <div className="space-y-2 sm:col-span-2">
      <div className="text-sm font-medium">{t("admin:recordTypes.fields.options")}</div>
      {options.map((o, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
          <Input aria-label={t("admin:recordTypes.fields.optionValue")} dir="ltr" className="font-mono" value={o.value} disabled={readOnly} placeholder={t("admin:recordTypes.fields.optionValue")} onChange={(e) => set(i, { value: e.target.value })} />
          <Input aria-label={t("admin:recordTypes.fields.label")} value={o.label} disabled={readOnly} placeholder={t("admin:recordTypes.fields.label")} onChange={(e) => set(i, { label: e.target.value })} />
          <Input aria-label={t("admin:recordTypes.fields.labelAr")} dir="rtl" value={o.labelAr ?? ""} disabled={readOnly} placeholder={t("admin:recordTypes.fields.labelAr")} onChange={(e) => set(i, { labelAr: e.target.value || null })} />
          {!readOnly ? <Button size="sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => onChange(options.filter((_, j) => j !== i))}><Trash2 /></Button> : <span />}
        </div>
      ))}
      {!readOnly ? <Button size="sm" variant="outline" onClick={() => onChange([...options, { value: `option${options.length + 1}`, label: "" }])}><Plus /> {t("admin:recordTypes.fields.addOption")}</Button> : null}
    </div>
  );
}

// ---------------------------------------------------------------- links

function LinksTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const types = useDefinitionList("record-types");
  const set = (i: number, p: Partial<RecordRelation>) => patch((b) => ({ ...b, relations: b.relations.map((r, j) => (j === i ? { ...r, ...p } : r)) }));
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:recordTypes.tabs.links")}</CardTitle><CardDescription>{t("admin:recordTypes.links.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-2">
        {body.relations.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:recordTypes.links.none")}</p> : null}
        {body.relations.map((r, i) => (
          <div key={i} className="grid items-end gap-2 rounded-xl border border-sand-200 p-3 md:grid-cols-[1fr_1fr_140px_1fr_auto_auto_auto]">
            <Field label={t("admin:recordTypes.fields.label")}><Input value={r.label} disabled={readOnly} onChange={(e) => { const label = e.target.value; set(i, /^link\d+$/.test(r.key) || r.key === fieldKeyFromLabel(r.label) ? { label, key: fieldKeyFromLabel(label) || r.key } : { label }); }} /></Field>
            <Field label={t("admin:recordTypes.fields.labelAr")}><Input dir="rtl" value={r.labelAr ?? ""} disabled={readOnly} onChange={(e) => set(i, { labelAr: e.target.value || null })} /></Field>
            <Field label={t("admin:common.key")}><Input dir="ltr" className="font-mono" value={r.key} disabled={readOnly} onChange={(e) => set(i, { key: e.target.value })} /></Field>
            <Field label={t("admin:recordTypes.links.target")}>
              <NativeSelect value={r.target} disabled={readOnly} onChange={(e) => set(i, { target: e.target.value })}>
                <option value="">—</option>
                <option value="case">{t("admin:recordTypes.links.case")}</option>
                {(types.data ?? []).map((x: DefinitionSummary) => <option key={x.key} value={x.key}>{locale === "ar" && x.nameAr ? x.nameAr : x.name}</option>)}
              </NativeSelect>
            </Field>
            <label className="flex h-9 items-center gap-2 text-sm"><Checkbox checked={!!r.many} disabled={readOnly} onCheckedChange={(c) => set(i, { many: c === true })} /> {t("admin:recordTypes.links.many")}</label>
            <label className="flex h-9 items-center gap-2 text-sm"><Checkbox checked={!!r.required} disabled={readOnly} onCheckedChange={(c) => set(i, { required: c === true })} /> {t("admin:recordTypes.fields.required")}</label>
            {!readOnly ? <Button size="sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, relations: b.relations.filter((_, j) => j !== i) }))}><Trash2 /></Button> : <span />}
          </div>
        ))}
        {!readOnly ? <Button data-testid="add-link" size="sm" variant="outline" onClick={() => patch((b) => ({ ...b, relations: [...b.relations, { key: `link${b.relations.length + 1}`, label: "", target: "case", many: false, required: false }] }))}><Plus /> {t("admin:recordTypes.links.add")}</Button> : null}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- checks

function conditionFields(fields: RecordField[], locale: string): ConditionField[] {
  return fields.map((f) => ({ key: f.key, label: fieldLabel(f, locale), type: f.type, options: f.options?.map((o) => ({ value: o.value, label: locale === "ar" && o.labelAr ? o.labelAr : o.label })) }));
}

function ChecksTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const fields = useMemo(() => conditionFields(body.fields, locale), [body.fields, locale]);
  const set = (i: number, p: Partial<RecordCheck>) => patch((b) => ({ ...b, rules: b.rules.map((r, j) => (j === i ? { ...r, ...p } : r)) }));
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:recordTypes.tabs.checks")}</CardTitle><CardDescription>{t("admin:recordTypes.checks.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        {body.rules.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:recordTypes.checks.none")}</p> : null}
        {body.rules.map((r, i) => (
          <div key={i} data-testid={`check-${r.key}`} className="space-y-3 rounded-xl border border-sand-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={r.severity === "warning" ? "warning" : "danger"} size="sm">{t(`admin:recordTypes.checks.severity.${r.severity ?? "error"}`)}</Badge>
              <span className="text-sm text-muted-foreground">{t("admin:recordTypes.checks.flagWhen")} {describeCondition(r.when, fields, t as T)}</span>
              {!readOnly ? <Button size="sm" variant="ghost" className="ms-auto" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, rules: b.rules.filter((_, j) => j !== i) }))}><Trash2 /></Button> : null}
            </div>
            <ConditionBuilder value={r.when} fields={fields} readOnly={readOnly} onChange={(when) => set(i, { when, fields: fieldsOf(when) })} />
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_160px]">
              <Field label={t("admin:recordTypes.checks.message")}><Input data-testid="check-message" value={r.messageEn} disabled={readOnly} onChange={(e) => set(i, { messageEn: e.target.value })} /></Field>
              <Field label={t("admin:recordTypes.checks.messageAr")}><Input dir="rtl" value={r.messageAr ?? ""} disabled={readOnly} onChange={(e) => set(i, { messageAr: e.target.value || null })} /></Field>
              <Field label={t("admin:recordTypes.checks.severityLabel")}>
                <NativeSelect value={r.severity ?? "error"} disabled={readOnly} onChange={(e) => set(i, { severity: e.target.value as "error" | "warning" })}>
                  <option value="error">{t("admin:recordTypes.checks.severity.error")}</option>
                  <option value="warning">{t("admin:recordTypes.checks.severity.warning")}</option>
                </NativeSelect>
              </Field>
            </div>
          </div>
        ))}
        {!readOnly ? <Button data-testid="add-check" size="sm" variant="outline" disabled={body.fields.length === 0} onClick={() => patch((b) => ({ ...b, rules: [...b.rules, { key: `check${b.rules.length + 1}`, when: { "!": { var: b.fields[0]?.key ?? "" } }, messageEn: "", messageAr: null, severity: "error", fields: b.fields[0] ? [b.fields[0].key] : [] }] }))}><Plus /> {t("admin:recordTypes.checks.add")}</Button> : null}
      </CardContent>
    </Card>
  );
}

function fieldsOf(expr: unknown): string[] {
  const out = new Set<string>();
  const walk = (n: unknown) => {
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (n && typeof n === "object") for (const [k, v] of Object.entries(n)) { if (k === "var" && typeof v === "string") out.add(v.split(".")[0]!); else walk(v); }
  };
  walk(expr);
  return [...out].slice(0, 1);
}

// ---------------------------------------------------------------- settings

function SettingsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const { body, patch, readOnly } = ctx;
  const workspaces = useWorkspaces();
  const services = useDefinitionList("services", {}, can("platform.services.read"));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>{t("admin:recordTypes.settings.about")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:common.name")} required><Input value={body.name} disabled={readOnly} onChange={(e) => patch({ name: e.target.value })} /></Field>
          <Field label={t("admin:common.nameAr")}><Input dir="rtl" value={body.nameAr ?? ""} disabled={readOnly} onChange={(e) => patch({ nameAr: e.target.value || null })} /></Field>
          <Field label={t("admin:common.description")}><Textarea value={body.description ?? ""} disabled={readOnly} onChange={(e) => patch({ description: e.target.value || null })} /></Field>
          <Field label={t("admin:common.descriptionAr")}><Textarea dir="rtl" value={body.descriptionAr ?? ""} disabled={readOnly} onChange={(e) => patch({ descriptionAr: e.target.value || null })} /></Field>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:recordTypes.settings.numbering")}</CardTitle><CardDescription>{t("admin:recordTypes.settings.numberingHint", { example: `${body.numberPrefix}-${new Date().getFullYear()}-0001` })}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:recordTypes.settings.prefix")}><Input data-testid="number-prefix" dir="ltr" className="font-mono" value={body.numberPrefix} disabled={readOnly} onChange={(e) => patch({ numberPrefix: e.target.value.toUpperCase() })} /></Field>
          <Field label={t("admin:recordTypes.settings.titleField")} hint={t("admin:recordTypes.settings.titleFieldHint")}>
            <NativeSelect value={body.titleField ?? ""} disabled={readOnly} onChange={(e) => patch({ titleField: e.target.value || null })}>
              <option value="">{t("admin:recordTypes.settings.numberAsTitle")}</option>
              {body.fields.map((f) => <option key={f.key} value={f.key}>{fieldLabel(f, locale)}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:recordTypes.settings.owner")} hint={t("admin:recordTypes.settings.ownerHint")}>
            <NativeSelect data-testid="department" value={body.departmentKey ?? ""} disabled={readOnly} onChange={(e) => patch({ departmentKey: e.target.value || null })}>
              <option value="">{t("admin:recordTypes.shared")}</option>
              {(workspaces.data ?? []).map((w) => <option key={w.key} value={w.key}>{locale === "ar" && w.nameAr ? w.nameAr : w.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:recordTypes.settings.extends")} hint={t("admin:recordTypes.settings.extendsHint")}>
            <NativeSelect data-testid="extends-service" value={body.extendsServiceKey ?? ""} disabled={readOnly} onChange={(e) => patch({ extendsServiceKey: e.target.value || null })}>
              <option value="">{t("admin:common.none")}</option>
              {(services.data ?? []).map((s) => <option key={s.key} value={s.key}>{locale === "ar" && s.nameAr ? s.nameAr : s.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:recordTypes.settings.tags")}><Input value={body.tags.join(", ")} disabled={readOnly} onChange={(e) => patch({ tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} /></Field>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- try

function TryTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const { body, definition, dirty, save } = ctx;
  const actions = useRecordActions();
  const [data, setData] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<{ valid: boolean; issues: RecordIssue[] } | null>(null);
  const published = definition.status === "Published";

  const check = async () => {
    // The check runs against the stored version: if the edits could not be stored, it does not check the older one.
    if (dirty && !(await save())) return;
    try {
      setResult(await actions.validate.mutateAsync({ type: definition.key, version: definition.version, data, locale }));
    } catch { /* useSave reported it */ }
  };
  const saveTest = async () => {
    try {
      const r = await actions.create.mutateAsync({ type: definition.key, data, isTest: true, locale });
      notifySaved(r, t("admin:recordTypes.try.saved", { number: r.number }));
      setData({}); setResult(null);
    } catch { /* useSave reported it */ }
  };

  if (body.fields.length === 0) return <EmptyState icon={FlaskConical} title={t("admin:recordTypes.fields.emptyTitle")} description={t("admin:recordTypes.try.needFields")} />;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
      <Card>
        <CardHeader><CardTitle>{t("admin:recordTypes.try.title")}</CardTitle><CardDescription>{t("admin:recordTypes.try.intro")}</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <RecordForm fields={body.fields} data={data} onChange={(d) => { setData(d); setResult(null); }} issues={result?.issues} />
          <div className="flex flex-wrap gap-2">
            <Button data-testid="try-check" variant="gold" loading={actions.validate.isPending} onClick={check}><ShieldCheck /> {t("admin:recordTypes.try.check")}</Button>
            {can("records.write") ? <Button variant="outline" disabled={!published} loading={actions.create.isPending} onClick={saveTest}><Rows3 /> {t("admin:recordTypes.try.saveTest")}</Button> : null}
          </div>
          {!published ? <p className="text-xs text-muted-foreground">{t("admin:recordTypes.try.publishFirst")}</p> : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:recordTypes.try.result")}</CardTitle></CardHeader>
        <CardContent data-testid="try-result">
          {!result ? <p className="text-sm text-muted-foreground">{t("admin:recordTypes.try.noResult")}</p> : result.valid ? (
            <InlineAlert tone="success" title={t("admin:recordTypes.try.valid")}>{t("admin:recordTypes.try.validBody")}</InlineAlert>
          ) : (
            <div className="space-y-2">
              <InlineAlert tone="danger" title={t("admin:recordTypes.try.invalid", { count: result.issues.length })} />
              <ul className="space-y-1.5 text-sm">
                {result.issues.map((i, n) => {
                  const f = body.fields.find((x) => i.path.replace(/^\$?\.?\/?/, "").startsWith(x.key));
                  return <li key={n} className="flex gap-2"><Badge variant={i.severity === "warning" ? "warning" : "danger"} size="sm">{f ? fieldLabel(f, locale) : i.path || "—"}</Badge><span>{locale === "ar" && i.messageAr ? i.messageAr : i.message}</span></li>;
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- records

interface TypeView { key: string; version: number; fields: RecordField[] }

function RecordsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const { can } = usePermission();
  const [q, setQ] = useState("");
  const [includeTest, setIncludeTest] = useState(true);
  const [editing, setEditing] = useState<DataRecord | "new" | null>(null);
  const records = useRecords({ type: ctx.definition.key, q: q || undefined, includeTest, take: 100 });
  const published = useQuery({ queryKey: ["records", "type", ctx.definition.key], queryFn: () => api.get<TypeView>(`/api/records/types/${encodeURIComponent(ctx.definition.key)}`), retry: false });
  const fields = published.data?.fields ?? [];
  const listed = fields.filter((f) => f.listed !== false).slice(0, 4);
  const rows = records.data?.rows ?? [];

  if (published.isError) return <EmptyState icon={Rows3} title={t("admin:recordTypes.records.notPublished")} description={t("admin:recordTypes.records.notPublishedBody")} />;
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <div><CardTitle>{t("admin:recordTypes.tabs.records")}</CardTitle><CardDescription>{t("admin:recordTypes.records.intro", { count: records.data?.total ?? 0 })}</CardDescription></div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:generic.search")} className="h-9 w-56 ps-8" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={includeTest} onCheckedChange={setIncludeTest} /> {t("admin:recordTypes.records.includeTest")}</label>
          {can("records.write") ? <Button data-testid="new-record" variant="gold" size="sm" onClick={() => setEditing("new")}><Plus /> {t("admin:recordTypes.records.new")}</Button> : null}
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:recordTypes.records.none")}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground"><tr>
                <th className="px-2 py-2 text-start font-medium">{t("admin:recordTypes.records.number")}</th>
                <th className="px-2 py-2 text-start font-medium">{t("admin:recordTypes.records.titleCol")}</th>
                {listed.map((f) => <th key={f.key} className="px-2 py-2 text-start font-medium">{fieldLabel(f, locale)}</th>)}
                <th className="px-2 py-2 text-start font-medium">{t("admin:common.updated")}</th>
              </tr></thead>
              <tbody className="divide-y divide-sand-100">
                {rows.map((r) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-aegold-50/40" onClick={() => setEditing(r)}>
                    <td className="px-2 py-2 font-mono text-xs" dir="ltr">{r.number}{r.isTest ? <Badge variant="outline" size="sm" className="ms-1">{t("admin:recordTypes.records.test")}</Badge> : null}{r.isArchived ? <Badge variant="default" size="sm" className="ms-1">{t("admin:recordTypes.records.archived")}</Badge> : null}</td>
                    <td className="px-2 py-2 font-medium">{r.title}</td>
                    {listed.map((f) => <td key={f.key} className="px-2 py-2 text-xs">{display(r.data?.[f.key], f, locale)}</td>)}
                    <td className="px-2 py-2 text-xs text-muted-foreground">{formatDateTime(r.updatedAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {editing ? <RecordDialog typeKey={ctx.definition.key} fields={fields} record={editing === "new" ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function display(v: unknown, f: RecordField, locale: string): string {
  if (v == null || v === "") return "—";
  if (f.type === "boolean") return v === true ? "✓" : "✗";
  if (f.type === "choice") { const o = f.options?.find((x) => x.value === v); return o ? (locale === "ar" && o.labelAr ? o.labelAr : o.label) : String(v); }
  if (f.type === "money" && typeof v === "number") return new Intl.NumberFormat(locale === "ar" ? "ar-AE" : "en-GB").format(v);
  return String(v);
}

function RecordDialog({ typeKey, fields, record, onClose }: { typeKey: string; fields: RecordField[]; record: DataRecord | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const actions = useRecordActions();
  const [data, setData] = useState<Record<string, unknown>>(record?.data ?? {});
  const [issues, setIssues] = useState<RecordIssue[]>([]);
  const readOnly = !can("records.write") || !!record?.isArchived;
  const submit = async () => {
    try {
      const r = record ? await actions.update.mutateAsync({ id: record.id, data, locale }) : await actions.create.mutateAsync({ type: typeKey, data, locale });
      notifySaved(r, t("admin:recordTypes.records.saved", { number: r.number }));
      onClose();
    } catch (e) {
      // useSave reported it with the server's words; the fields it names are marked in the form.
      const problem = (e as { problem?: { issues?: RecordIssue[] } }).problem;
      if (problem?.issues) setIssues(problem.issues);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{record ? `${record.number} · ${record.title}` : t("admin:recordTypes.records.new")}</DialogTitle></DialogHeader>
        <RecordForm fields={fields} data={data} onChange={setData} issues={issues} readOnly={readOnly} />
        <DialogFooter>
          {record && can("records.write") ? <Button variant="ghost" className="me-auto" loading={actions.archive.isPending} onClick={() => actions.archive.mutate({ id: record.id, archived: !record.isArchived }, { onSuccess: (r) => { notifySaved(r, t("admin:recordTypes.records.saved", { number: r.number })); onClose(); } })}>{record.isArchived ? t("admin:recordTypes.records.restore") : t("admin:recordTypes.records.archive")}</Button> : null}
          <Button variant="outline" onClick={onClose}>{t("admin:common.cancel")}</Button>
          {!readOnly ? <Button data-testid="record-save" variant="gold" loading={actions.create.isPending || actions.update.isPending} onClick={submit}><CheckCircle2 /> {t("admin:common.save")}</Button> : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
